import React, { forwardRef } from "react";
import "./OneClickSection.css";
import { motion } from "framer-motion";
import { MdOutlineTrackChanges, MdOutlineCampaign, MdInsights } from "react-icons/md";

const fadeUp = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.7, ease: "easeOut" },
  viewport: { once: true, amount: 0.25 },
};

const cards = [
  {
    icon: <MdOutlineTrackChanges />,
    step: "Step 01",
    title: "Define success",
    desc: "We align on goals, audiences, and constraints before spend. That includes realistic KPIs and what “good” looks like for your business.",
    bullets: ["Goal workshop", "Budget reality check", "Tracking readiness"],
  },
  {
    icon: <MdOutlineCampaign />,
    step: "Step 02",
    title: "Launch with quality",
    desc: "Campaigns, content, and tags go live with checklists—so ads, landing pages, and analytics tell a consistent story.",
    bullets: ["Creative & copy QA", "UTM + event naming", "Policy-friendly claims"],
  },
  {
    icon: <MdInsights />,
    step: "Step 03",
    title: "Learn and iterate",
    desc: "We review data on a regular cadence, document learnings, and adjust. Improvement is the aim—not guaranteed lift every week.",
    bullets: ["Readable dashboards", "Test backlog", "Honest readouts"],
  },
];

const OneClickSection = forwardRef((_, ref) => {
  return (
    <section className="ocs" ref={ref} aria-label="How we work with clients">
      <div className="ocs__wrap">
        <motion.div className="ocs__head" {...fadeUp}>
          <div>
            <div className="ocs__badge">
              <span className="dot" aria-hidden="true" />
              Engagement model
            </div>

            <h2 className="ocs__title">
              From plan to <span>proof</span>—without hype.
            </h2>

            <p className="ocs__sub">
              Whether you need a one-time audit or ongoing management, we keep communication and reporting straightforward.
              You always know what we control versus what platforms decide.
            </p>
          </div>

          <div className="ocs__aside">
            <b>Typical rhythm:</b>
            <p>
              Kickoff &amp; access → baseline &amp; launch → weekly or biweekly check-ins → monthly strategy review.
            </p>
          </div>
        </motion.div>

        <div className="ocs__grid">
          {cards.map((c) => (
            <motion.div key={c.title} className="ocs__card" {...fadeUp}>
              <div className="ocs__top">
                <div className="ocs__icon" aria-hidden="true">
                  {c.icon}
                </div>
                <div className="ocs__step">{c.step}</div>
              </div>

              <h3 className="ocs__h4">{c.title}</h3>
              <p className="ocs__p">{c.desc}</p>

              <ul className="ocs__bullets">
                {c.bullets.map((b, idx) => (
                  <li key={idx}>{b}</li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
});

OneClickSection.displayName = "OneClickSection";

export default OneClickSection;
